"use client";

import { useCallback, useEffect, useState } from "react";
import { PURCHASE_COMPLETE_EVENT } from "@owlcat/app-shell";
import { getMazeImageQuota } from "@/contexts/games/actions/getMazeImageQuota";

/**
 * Photo-maze quota for the maze UI ("you can make N more mazes") — the games
 * allowance viewed in MAZES, never tokens (subscription-first / hidden tokens).
 * `undefined` while loading / for anon; `null` when accounts can't be read (the
 * UI then lets the upload through and the server reserve decides). `refresh()`
 * re-reads after a spend or a top-up.
 */
export function useMazeImageQuota(): {
  remaining: number | null | undefined;
  /** Admin-set onboarding gift size (mazes); undefined until the first read. */
  giftMazes: number | undefined;
  refresh: () => void;
  setRemaining: (n: number) => void;
} {
  const [remaining, setRemaining] = useState<number | null | undefined>(undefined);
  const [giftMazes, setGiftMazes] = useState<number | undefined>(undefined);

  const refresh = useCallback(() => {
    let alive = true;
    getMazeImageQuota()
      .then((q) => {
        if (!alive) return;
        setRemaining(q.remaining);
        setGiftMazes(q.giftMazes);
      })
      .catch(() => {
        if (alive) setRemaining(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => refresh(), [refresh]);

  // A top-up happens in the shared <BuyTokensWidget> (Stripe modal / Telegram
  // Stars). The widget fires `owlcat:purchase-complete` once the grant has
  // landed; tab focus/visibility is the fallback (Stars paid in the Telegram app).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onPurchase = () => refresh();
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(PURCHASE_COMPLETE_EVENT, onPurchase);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(PURCHASE_COMPLETE_EVENT, onPurchase);
    };
  }, [refresh]);

  return { remaining, giftMazes, refresh, setRemaining };
}
